import React, { useState, useMemo, useEffect } from 'react';
import Layout from '../../components/Layout';
import MoveDetailView from './MoveDetailView';
import { fetchSalesMonitoringSummary } from '../../api/salesMonitoring';
import './SalesMonitoringResults.css';

// Tri par defaut (plus de selecteurs Order by dans l'UI) : Game groupe d'abord
// (BOR ensemble, puis L3, MAR, ...), puis Value desc, puis Number asc dans chaque groupe.
const sortData = (data) => {
  return [...data].sort((a, b) => {
    if (a.game !== b.game) return a.game.localeCompare(b.game);
    if (b.value !== a.value) return b.value - a.value;
    return parseInt(a.numero, 10) - parseInt(b.numero, 10);
  });
};

const formatDateDisplay = (d) => {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
};

const SalesMonitoringResults = ({ date, tirage, filter, scopeId, onBack }) => {
  const [selectedRow, setSelectedRow] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    fetchSalesMonitoringSummary({ tirage, date, filter, scope: scopeId })
      .then((data) => { if (!cancelled) setSummary(data); })
      .catch(() => { if (!cancelled) setError('Impossible de charger les ventes — verifie que le serveur backend tourne.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tirage, date, filter, scopeId]);

  const productSummary = summary ? summary.product_summary : [];
  const lotteryData = summary ? summary.lottery_summary : [];
  const tirageName = summary ? summary.tirage_name : '';

  const sortedData = useMemo(
    () => sortData(summary ? summary.numbers : []),
    [summary]
  );

  if (selectedRow) {
    return (
      <MoveDetailView
        row={selectedRow}
        date={formatDateDisplay(date)}
        tirage={tirage}
        tirageName={tirageName}
        filter={filter}
        scopeId={scopeId}
        rawDate={date}
        onBack={() => setSelectedRow(null)}
      />
    );
  }

  const totalValue = sortedData.reduce((s, r) => s + r.value, 0);
  const totalBets  = sortedData.reduce((s, r) => s + r.bets, 0);
  const totalProduct = productSummary.reduce((s, r) => s + r.value, 0);
  const totalLottery = lotteryData.reduce((s, r) => s + r.total, 0);
  const totalLocal = lotteryData.reduce((s, r) => s + r.local, 0);

  return (
    <Layout>
      <div className="smr-info-bar">
        Date : {formatDateDisplay(date)} | Lottery : {tirageName} | :
      </div>

      {error && <p className="smr-error">{error}</p>}
      {loading && <p className="smr-loading">Chargement...</p>}

      {!loading && !error && (
        <div className="smr-body">
          {/* Tableau gauche */}
          <div className="smr-left">
            <table className="smr-table">
              <thead>
                <tr>
                  <th>Game</th>
                  <th>Numeros</th>
                  <th>Value</th>
                  <th>Number of Bets</th>
                  <th>Average</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((row, i) => (
                  <tr key={i}>
                    <td>{row.game}</td>
                    <td>
                      <span
                        className="smr-numero smr-numero-link"
                        onClick={() => setSelectedRow(row)}
                      >{row.numero}</span>
                    </td>
                    <td className={row.hot ? 'smr-hot' : ''}>{row.value.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td>{row.bets}</td>
                    <td>{row.avg.toFixed(2)}</td>
                  </tr>
                ))}
                <tr className="smr-total-row">
                  <td colSpan="2"><strong>Total</strong></td>
                  <td className="smr-hot"><strong>{totalValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong></td>
                  <td><strong>{totalBets}</strong></td>
                  <td><strong>{totalBets > 0 ? (totalValue / totalBets).toFixed(2) : '0.00'}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Panneau droit */}
          <div className="smr-right">
            {/* Resume par produit */}
            <div className="smr-lottery-box">
              <div className="smr-lottery-header">
                <div className="smr-lottery-title">{tirageName}</div>
                <div className="smr-lottery-actions">
                  <button className="smr-back-btn" onClick={onBack}>↩</button>
                  <button className="smr-menu-btn">☰<br/><span>Menu</span></button>
                </div>
              </div>
              <table className="smr-summary-table">
                <tbody>
                  {productSummary.map((row, i) => (
                    <tr key={i}>
                      <td className="smr-sum-label">{row.label}</td>
                      <td className="smr-sum-value">{row.value.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                  <tr className="smr-sum-total">
                    <td>Total:</td>
                    <td>{totalProduct.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Summary by Lottery */}
            <div className="smr-bylottery-box">
              <div className="smr-bylottery-title">Summary by Lottery</div>
              <table className="smr-bylottery-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Lot</th>
                    <th>Description</th>
                    <th>Local</th>
                    <th>Online</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {lotteryData.map((row, i) => (
                    <tr key={i}>
                      <td>🔒</td>
                      <td>{row.lot}</td>
                      <td>{row.desc}</td>
                      <td>{row.local.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                      <td className="smr-online-val">{row.online.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                      <td className="smr-online-val">{row.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                  <tr className="smr-bylottery-total">
                    <td></td>
                    <td colSpan="2"><strong>Total</strong></td>
                    <td>{totalLocal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td className="smr-online-val">{totalLottery.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td className="smr-online-val">{totalLottery.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default SalesMonitoringResults;
